/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var core_1 = require("@angular/core");
var rxjs_1 = require("rxjs");
var kendo_angular_l10n_1 = require("@progress/kendo-angular-l10n");
var kendo_angular_common_1 = require("@progress/kendo-angular-common");
var kendo_licensing_1 = require("@progress/kendo-licensing");
var package_metadata_1 = require("../package-metadata");
var label_directive_1 = require("./../label.directive");
var util_1 = require("./../util");
/**
 * Represents the [Kendo UI Label component for Angular]({% slug label_basic %}).
 *
 * Associates a label with input elements or components.
 *
 * @example
 * ```ts
 *
 * _@Component({
 *   selector: 'my-app',
 *   template: `
 *     <kendo-label [for]="input" text="First name">
 *       <input [(ngModel)]="name" kendoTextBox #input />
 *     </kendo-label>
 *   `
 * })
 * class AppComponent {
 *     public name = 'John';
 * }
 *
 * ```
 */
var LabelComponent = /** @class */ (function () {
    function LabelComponent(elementRef, renderer, localization) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.localization = localization;
        this.subscriptions = new rxjs_1.Subscription();
        kendo_licensing_1.validatePackage(package_metadata_1.packageMetadata);
        this.direction = localization.rtl ? 'rtl' : 'ltr';
        this.renderer.removeAttribute(this.elementRef.nativeElement, 'id');
    }
    /**
     * @hidden
     */
    LabelComponent.prototype.ngAfterContentInit = function () {
        if (this.for) {
            this.control = this.for;
            return;
        }
        var wrappedNativeInput = util_1.getWrappedNativeInput(this.elementRef.nativeElement);
        if (wrappedNativeInput) {
            if (!wrappedNativeInput.hasAttribute('id')) {
                this.renderer.setAttribute(wrappedNativeInput, 'id', "k-" + kendo_angular_common_1.guid());
            }
            this.control = wrappedNativeInput;
            return;
        }
        this.control = this.kendoInput;
    };
    /**
     * @hidden
     */
    LabelComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.subscriptions.add(this.localization.changes.subscribe(function (_a) {
            var rtl = _a.rtl;
            _this.direction = rtl ? 'rtl' : 'ltr';
        }));
    };
    /**
     * @hidden
     */
    LabelComponent.prototype.ngAfterViewInit = function () {
        this.labelDirective.setAriaLabelledby();
    };
    /**
     * @hidden
     */
    LabelComponent.prototype.ngOnDestroy = function () {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    };
    /**
     * @hidden
     */
    LabelComponent.prototype.textFor = function (key) {
        return this.localization.get(key);
    };
    tslib_1.__decorate([
        core_1.HostBinding('attr.dir'),
        tslib_1.__metadata("design:type", String)
    ], LabelComponent.prototype, "direction", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], LabelComponent.prototype, "text", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object)
    ], LabelComponent.prototype, "for", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], LabelComponent.prototype, "optional", void 0);
    tslib_1.__decorate([
        core_1.ViewChild(label_directive_1.LabelDirective, { static: true }),
        tslib_1.__metadata("design:type", label_directive_1.LabelDirective)
    ], LabelComponent.prototype, "labelDirective", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(kendo_angular_common_1.KendoInput, { static: true }),
        tslib_1.__metadata("design:type", Object)
    ], LabelComponent.prototype, "kendoInput", void 0);
    LabelComponent = tslib_1.__decorate([
        core_1.Component({
            selector: 'kendo-label',
            exportAs: 'kendoLabel',
            providers: [
                kendo_angular_l10n_1.LocalizationService,
                {
                    provide: kendo_angular_l10n_1.L10N_PREFIX,
                    useValue: 'kendo.label'
                }
            ],
            template: "\n        <ng-container kendoLabelLocalizedMessages\n            i18n-optional=\"kendo.label.optional|The text for the optional segment of a Label component\"\n            optional=\"Optional\"\n         >\n        </ng-container>\n        <label\n            [for]=\"control\"\n            [class.k-label-empty]=\"!text\">\n            {{ text }}<span *ngIf=\"optional\" class=\"k-label-optional\">({{textFor('optional')}})</span>\n        </label>\n        <ng-content></ng-content>\n    "
        }),
        tslib_1.__metadata("design:paramtypes", [core_1.ElementRef,
            core_1.Renderer2,
            kendo_angular_l10n_1.LocalizationService])
    ], LabelComponent);
    return LabelComponent;
}());
exports.LabelComponent = LabelComponent;
